'use client';

import { memo, useMemo, type ReactNode } from 'react';

import Image from 'next/image';
import { BadgeCheck } from 'lucide-react';

import {
	XIcon,
	XPostCopyLinkIcon,
	XPostLikeIcon,
	XPostReplyIcon,
} from '@/components/icons/svgs';

import { cn } from '@/lib/utils';

import type { LandingTweet } from '../../constants/landing-constants';

const TWEET_ENTITY =
	/(@[A-Za-z0-9_]+)|(https?:\/\/[^\s]+)|((?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/[^\s]*)?)/gi;

const renderTweetBody = (body: string, href: string) => {
	const nodes: ReactNode[] = [];
	let lastIndex = 0;

	for (const match of body.matchAll(TWEET_ENTITY)) {
		const value = match[0];
		const index = match.index;

		if (index === undefined) continue;

		if (index > lastIndex) {
			nodes.push(body.slice(lastIndex, index));
		}

		nodes.push(
			<a
				key={`${value}-${index}`}
				href={href}
				target="_blank"
				rel="noopener noreferrer"
				className="text-[#1d9bf0] hover:underline"
			>
				{value}
			</a>
		);

		lastIndex = index + value.length;
	}

	if (lastIndex < body.length) {
		nodes.push(body.slice(lastIndex));
	}

	return nodes;
};

type TweetEmbedProps = {
	tweet: LandingTweet;
};

const formatMetric = (value: number) => {
	if (value < 1000) return String(value);

	const thousands = value / 1000;
	const formatted = thousands >= 10 ? thousands.toFixed(0) : thousands.toFixed(1).replace(/\.0$/, '');

	return `${formatted}K`;
};

const ACTION_ICON_CLASS = 'size-5 h-lh shrink-0';

export const TweetEmbed = memo(({ tweet }: TweetEmbedProps) => {
	const body = useMemo(() => renderTweetBody(tweet.body, tweet.href), [tweet.body, tweet.href]);

	return (
		<article
			className="flex h-full w-[22.5rem] shrink-0 flex-col gap-3 rounded-xl bg-white p-4 ring-1 ring-[#cfd9de] md:w-[24rem]"
			aria-label={`Post by ${tweet.name}`}
		>
			<div className="flex gap-3">
				<Image
					src={tweet.avatar}
					alt=""
					width={48}
					height={48}
					className="size-12 shrink-0 rounded-full object-cover outline-1 -outline-offset-1 outline-black/10"
				/>

				<div className="flex min-w-0 flex-1 items-start justify-between gap-3">
					<a
						href={tweet.href}
						target="_blank"
						rel="noopener noreferrer"
						className="min-w-0"
						aria-label={`${tweet.name}, @${tweet.handle}. Opens in a new tab`}
					>
						<div>
							<div className="flex items-center gap-0.5">
								<p className="truncate text-base font-semibold text-neutral-950">{tweet.name}</p>
								{tweet.verified ? (
									<BadgeCheck
										className="size-5 shrink-0 fill-[#1d9bf0] text-white"
										aria-hidden="true"
									/>
								) : null}
							</div>
							<p className="truncate text-base text-neutral-500">@{tweet.handle}</p>
						</div>
					</a>

					<a
						href={tweet.href}
						target="_blank"
						rel="noopener noreferrer"
						aria-label="View on X. Opens in a new tab"
					>
						<XIcon
							className="size-5 shrink-0 fill-neutral-950"
							aria-hidden="true"
						/>
					</a>
				</div>
			</div>

			<p className="min-h-0 flex-1 whitespace-pre-wrap text-pretty text-base text-neutral-950">{body}</p>

			<p className="text-base text-neutral-500">{tweet.postedAt}</p>

			<div className="flex items-center gap-5 border-t border-[#cfd9de] pt-3 text-neutral-500">
				<p className="flex items-center gap-1 text-base font-semibold tabular-nums sm:text-sm">
					<XPostLikeIcon className={cn(ACTION_ICON_CLASS, 'fill-[#f91880]')} />
					<span className="sr-only">Likes</span>
					{formatMetric(tweet.likes)}
				</p>
				<p className="flex items-center gap-1 text-base font-semibold sm:text-sm">
					<XPostReplyIcon className={cn(ACTION_ICON_CLASS, 'fill-[#1d9bf0]')} />
					Reply
				</p>
				<p className="flex items-center gap-1 text-base font-semibold sm:text-sm">
					<XPostCopyLinkIcon className={cn(ACTION_ICON_CLASS, 'fill-current')} />
					Copy link
				</p>
			</div>
		</article>
	);
});

TweetEmbed.displayName = 'TweetEmbed';
